import DownloadIcon from "@mui/icons-material/Download";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  TablePagination,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import MDBox from "components/MDBox";
import { downloadCsv } from "./downloadUtils";
import { ASSISTANT_EXPANDED_DIALOG_Z_INDEX } from "./constants";

const ASSISTANT_TABLE_CLASS = "assistant-chat-table";
const DEFAULT_PAGE_SIZE = 10;
const ROWS_PER_PAGE_OPTIONS = [10, 15, 25, 50];

function getTableLayoutSx() {
  return {
    width: "100%",
    [`& .${ASSISTANT_TABLE_CLASS}__scroll`]: {
      width: "100%",
    },
    [`& .${ASSISTANT_TABLE_CLASS}__header,
      & .${ASSISTANT_TABLE_CLASS}__row`]: {
      display: "grid",
      width: "100%",
    },
    [`& .${ASSISTANT_TABLE_CLASS}__header`]: {
      position: "sticky",
      top: 0,
      zIndex: 1,
      bgcolor: "background.paper",
      borderBottom: "1px solid rgba(148, 163, 184, 0.2)",
    },
    [`& .${ASSISTANT_TABLE_CLASS}__header-cell`]: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "4px",
      width: "100%",
      minWidth: 0,
      border: "none",
      background: "transparent",
      cursor: "pointer",
      py: 0.95,
      px: 1.5,
      fontSize: "0.72rem",
      fontWeight: 700,
      lineHeight: 1.2,
      color: "text.primary",
      textAlign: "center",
      whiteSpace: "nowrap",
    },
    [`& .${ASSISTANT_TABLE_CLASS}__row`]: {
      borderBottom: "1px solid rgba(148, 163, 184, 0.16)",
    },
    [`& .${ASSISTANT_TABLE_CLASS}__cell`]: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      minWidth: 0,
      py: 0.85,
      px: 1.5,
      fontSize: "0.71rem",
      lineHeight: 1.35,
      color: "text.secondary",
      textAlign: "center",
      wordBreak: "break-word",
    },
    [`& .${ASSISTANT_TABLE_CLASS}__empty`]: {
      py: 2,
      textAlign: "center",
      fontSize: "0.72rem",
      color: "text.secondary",
    },
  };
}

function formatCellValue(value, format) {
  if (format === "currency" && typeof value === "number") {
    return `ZAR ${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  if (format === "percent" && typeof value === "number") {
    return `${(value * 100).toFixed(1)}%`;
  }
  return String(value ?? "");
}

function compareValues(a, b) {
  const aNum = Number(a);
  const bNum = Number(b);
  if (Number.isFinite(aNum) && Number.isFinite(bNum)) {
    return aNum - bNum;
  }
  return String(a ?? "").localeCompare(String(b ?? ""));
}

function SortIcon({ direction }) {
  if (direction === "asc") return <ArrowUpwardIcon sx={{ fontSize: 14 }} />;
  if (direction === "desc") return <ArrowDownwardIcon sx={{ fontSize: 14 }} />;
  return <UnfoldMoreIcon sx={{ fontSize: 14, opacity: 0.45 }} />;
}

export default function TableRenderer({ title, columns = [], rows = [] }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState({ key: null, direction: "asc" });

  const safeRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const normalizedColumns = useMemo(() => {
    if (Array.isArray(columns) && columns.length > 0) {
      return columns
        .map((column) => {
          if (typeof column === "string") return { key: column, label: column };
          if (!column || typeof column !== "object") return null;
          const key = column.key ?? column.id ?? column.field ?? column.name;
          if (!key) return null;
          return { ...column, key, label: column.label ?? column.title ?? key };
        })
        .filter(Boolean);
    }

    const firstObjectRow = safeRows.find(
      (row) => row && typeof row === "object" && !Array.isArray(row)
    );
    return firstObjectRow
      ? Object.keys(firstObjectRow).map((key) => ({ key, label: key }))
      : [];
  }, [columns, safeRows]);

  const csvColumns = useMemo(
    () =>
      normalizedColumns.map((column) => ({
        key: column.key,
        label: column.label ?? column.key,
      })),
    [normalizedColumns]
  );

  const sortedRows = useMemo(() => {
    if (!sort.key) return safeRows;
    const sorted = [...safeRows];
    sorted.sort((rowA, rowB) => {
      const cmp = compareValues(rowA?.[sort.key], rowB?.[sort.key]);
      return sort.direction === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [safeRows, sort]);

  const pagedRows = useMemo(() => {
    const start = pageIndex * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, pageIndex, pageSize]);

  const gridTemplateColumns = useMemo(
    () => `repeat(${Math.max(normalizedColumns.length, 1)}, minmax(0, 1fr))`,
    [normalizedColumns.length]
  );

  if (!normalizedColumns.length) return null;

  const handleDownload = () => {
    downloadCsv({
      columns: csvColumns,
      rows: safeRows,
      filename: `${(title || "table").toLowerCase().replace(/\s+/g, "-")}.csv`,
    });
  };

  const handleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return { key: null, direction: "asc" };
    });
    setPageIndex(0);
  };

  const renderTable = (maxHeight) => (
    <Box className={ASSISTANT_TABLE_CLASS}>
      <Box
        className={`${ASSISTANT_TABLE_CLASS}__scroll`}
        sx={{ overflow: "auto", maxHeight }}
      >
        <Box
          className={`${ASSISTANT_TABLE_CLASS}__header`}
          sx={{ gridTemplateColumns }}
        >
          {normalizedColumns.map((column) => (
            <Box
              key={column.key}
              component="button"
              type="button"
              className={`${ASSISTANT_TABLE_CLASS}__header-cell`}
              onClick={() => handleSort(column.key)}
            >
              <span>{column.label ?? column.key}</span>
              <SortIcon
                direction={
                  sort.key === column.key ? sort.direction : null
                }
              />
            </Box>
          ))}
        </Box>

        {pagedRows.map((row, rowIndex) => (
          <Box
            key={rowIndex}
            className={`${ASSISTANT_TABLE_CLASS}__row`}
            sx={{
              gridTemplateColumns,
              bgcolor:
                rowIndex % 2 === 1
                  ? "rgba(148, 163, 184, 0.06)"
                  : "transparent",
            }}
          >
            {normalizedColumns.map((column) => (
              <Box
                key={column.key}
                className={`${ASSISTANT_TABLE_CLASS}__cell`}
              >
                {formatCellValue(row?.[column.key], column.format)}
              </Box>
            ))}
          </Box>
        ))}

        {!safeRows.length ? (
          <Box className={`${ASSISTANT_TABLE_CLASS}__empty`}>
            No data available.
          </Box>
        ) : null}
      </Box>

      <TablePagination
        component="div"
        count={safeRows.length}
        page={pageIndex}
        onPageChange={(_, nextPage) => setPageIndex(nextPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(event) => {
          setPageSize(Number(event.target.value));
          setPageIndex(0);
        }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        sx={{
          ".MuiTablePagination-toolbar": { minHeight: 40 },
          ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
            fontSize: "0.72rem",
            m: 0,
          },
        }}
      />
    </Box>
  );

  return (
    <MDBox
      className={ASSISTANT_TABLE_CLASS}
      mt={1}
      sx={{
        borderRadius: "12px",
        overflow: "hidden",
        border: "1px solid rgba(59, 130, 246, 0.18)",
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
        ...getTableLayoutSx(),
      }}
    >
      <MDBox
        sx={{
          px: 1.5,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(59, 130, 246, 0.14)",
        }}
      >
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 600 }}>
          {title || "Table"}
        </Typography>
        <MDBox sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          <Tooltip title="Download CSV">
            <IconButton
              size="small"
              onClick={handleDownload}
              aria-label="download table"
            >
              <DownloadIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Expand">
            <IconButton
              size="small"
              onClick={() => setIsExpanded(true)}
              aria-label="expand table"
            >
              <OpenInFullIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </MDBox>
      </MDBox>
      {renderTable(320)}
      <Dialog
        open={isExpanded}
        onClose={() => setIsExpanded(false)}
        maxWidth="xl"
        fullWidth
        sx={{ zIndex: ASSISTANT_EXPANDED_DIALOG_Z_INDEX }}
      >
        <DialogContent sx={{ p: 1.5 }}>
          <Box className={ASSISTANT_TABLE_CLASS} sx={getTableLayoutSx()}>
            {renderTable("70vh")}
          </Box>
        </DialogContent>
      </Dialog>
    </MDBox>
  );
}
